// src/lib/leasePeriod.js — the PURE calendar rules behind the contract composer's ONE linked
// lease control: start · duration · end (owner ruling 2026-09-11). No React, no i18n, so
// `node --test src/lib/leasePeriod.test.mjs` can run it.
//
// The ruling, verbatim:
//   "Make lease_start, lease_duration, and lease_end one linked control.
//    start + duration → derive end; start + manually entered end → derive duration;
//    editing duration recalculates end; editing end recalculates duration;
//    never silently overwrite the field the agent just edited;
//    show calculated fields as 'ავტომატურად';
//    duration uses calendar arithmetic, not months × 30 days;
//    for a 6-month lease starting 20.02.2026, the last included rental day is 19.08.2026;
//    store the actual start_date and end_date as contract facts; duration is derived/displayed
//    from them; if the interval is not an exact number of months, display the real duration,
//    e.g. 6 თვე და 12 დღე, rather than rounding."
//
// So the STORED facts are the two dates (values.lease_start / values.lease_end); the duration
// is a view of them — `diffPeriod` — and a control that WRITES the end through `addPeriod`.
//
// THE ONE CALENDAR RULE. A lease of n months from `start` ENDS the day before `start + n
// months`: the boundary is the first day NOT rented, and the end is the last day that is.
//   • Months and years add to the calendar month, and a start day the target month does not
//     have is CLAMPED to that month's last day BEFORE the day is subtracted:
//     2026-01-31 + 1 month → boundary 2026-02-28 → end 2026-02-27 (not "March 3rd − 1").
//   • Days are plain: 2026-02-20 + 10 days → boundary 2026-03-02 → end 2026-03-01.
//   • `diffPeriod` is the inverse: the largest whole number of calendar months whose
//     boundary does not pass end + 1 day, and the remaining days after it — so a computed end
//     always derives back to the duration that made it (pinned by the round-trip test), and a
//     hand-typed end reads as its REAL length ("6 თვე და 12 დღე"), never a rounded one.
//
// Dates are ISO `YYYY-MM-DD` strings in and out (the composer's contract with DateInput);
// anything unparseable → '' / null. All arithmetic is in UTC so a DST wall-clock never
// shifts a date by a day.

export const LEASE_UNITS = ['day', 'month', 'year']
export const LEASE_PRESET_MONTHS = [3, 6, 12]

const ISO = /^(\d{4})-(\d{2})-(\d{2})$/
export function parseIso(iso) {
  const m = ISO.exec(String(iso || ''))
  if (!m) return null
  const y = Number(m[1]), mo = Number(m[2]), d = Number(m[3])
  if (mo < 1 || mo > 12 || d < 1 || d > daysInMonth(y, mo)) return null
  return { y, mo, d }
}
export const daysInMonth = (y, mo) => new Date(Date.UTC(y, mo, 0)).getUTCDate()
const toIso = dt => dt.toISOString().slice(0, 10)
const utc = ({ y, mo, d }) => Date.UTC(y, mo - 1, d)
const DAY_MS = 86400000

// `start` + n calendar months, the day clamped to the target month's last day.
// The BOUNDARY of a lease (the first day not rented), not its end.
function addMonthsClamped(p, n) {
  const total = p.mo - 1 + n
  const y = p.y + Math.floor(total / 12), mo = ((total % 12) + 12) % 12 + 1
  return { y, mo, d: Math.min(p.d, daysInMonth(y, mo)) }
}
function addDays(p, n) {
  return fromUtc(utc(p) + n * DAY_MS)
}
function fromUtc(ms) {
  const dt = new Date(ms)
  return { y: dt.getUTCFullYear(), mo: dt.getUTCMonth() + 1, d: dt.getUTCDate() }
}
const cmp = (a, b) => utc(a) - utc(b)

// The first day NOT rented for a lease of n `unit`s from `start` — the boundary.
function boundaryOf(p, n, unit) {
  if (unit === 'day') return addDays(p, n)
  if (unit === 'year') return addMonthsClamped(p, 12 * n)
  return addMonthsClamped(p, n)
}

// The inclusive END of a lease of `n` `unit`s ('day' | 'month' | 'year') from `startIso`:
// (start + n units) − 1 day, calendar arithmetic. Bad input → ''.
export function addPeriod(startIso, n, unit = 'month') {
  const p = parseIso(startIso)
  const k = Number(n)
  if (!p || !Number.isInteger(k) || k < 1 || !LEASE_UNITS.includes(unit)) return ''
  return toIso(new Date(utc(addDays(boundaryOf(p, k, unit), -1))))
}

// The real length of [startIso, endIso] (both inclusive) as `{months, days, exactMonths}`:
// `months` = the largest whole calendar months from the start whose boundary does not pass
// end + 1 day, `days` = what is left. `exactMonths` = at least one month and no leftover
// days, i.e. `addPeriod(start, months, 'month') === end`. Bad input, or an end before the
// start → null. A one-day lease (end === start) is {months:0, days:1}.
export function diffPeriod(startIso, endIso) {
  const s = parseIso(startIso), e = parseIso(endIso)
  if (!s || !e || cmp(e, s) < 0) return null
  const boundary = addDays(e, 1)
  // Whole months: start from the calendar-month distance and step down while the boundary
  // overshoots (the clamp can only make a month SHORTER, so at most a step or two).
  let months = (boundary.y - s.y) * 12 + (boundary.mo - s.mo)
  if (boundary.d < s.d) months -= 1
  if (months < 0) months = 0
  while (months > 0 && cmp(addMonthsClamped(s, months), boundary) > 0) months -= 1
  while (cmp(addMonthsClamped(s, months + 1), boundary) <= 0) months += 1
  const days = Math.round((utc(boundary) - utc(addMonthsClamped(s, months))) / DAY_MS)
  return { months, days, exactMonths: months >= 1 && days === 0 }
}

// The duration CONTROL's `{n, unit}` for an interval, or null when it is not a whole number
// of the unit and must be shown as text instead. `preferUnit` keeps the agent's chosen unit
// when the interval fits it ('year' for 12/24/…, 'day' for a lease under a month or one that
// is whole days) and falls back to months.
export function asPeriod(diff, preferUnit = 'month') {
  if (!diff) return null
  const { months, days } = diff
  if (months >= 1 && days === 0) {
    if (preferUnit === 'year' && months % 12 === 0) return { n: months / 12, unit: 'year' }
    return { n: months, unit: 'month' }
  }
  if (months === 0 && days >= 1) return { n: days, unit: 'day' }
  return null
}

// ── Words ─────────────────────────────────────────────────────────────────────────────────
const LANGS = ['ka', 'en', 'ru']
export const normLang = lang => (LANGS.includes(lang) ? lang : 'ka')

// Russian counts decline: 1 месяц · 2–4 месяца · 5+ месяцев (and 11–14 always the genitive plural).
function ruPlural(n, one, few, many) {
  const mod10 = n % 10, mod100 = n % 100
  if (mod100 >= 11 && mod100 <= 14) return many
  if (mod10 === 1) return one
  if (mod10 >= 2 && mod10 <= 4) return few
  return many
}
const UNIT_WORD = {
  ka: { day: () => 'დღე', month: () => 'თვე', year: () => 'წელი' },
  en: { day: n => (n === 1 ? 'day' : 'days'), month: n => (n === 1 ? 'month' : 'months'), year: n => (n === 1 ? 'year' : 'years') },
  ru: {
    day: n => ruPlural(n, 'день', 'дня', 'дней'),
    month: n => ruPlural(n, 'месяц', 'месяца', 'месяцев'),
    year: n => ruPlural(n, 'год', 'года', 'лет'),
  },
}
const AND = { ka: 'და', en: 'and', ru: 'и' }

// "6 თვე" / "6 months" / "6 месяцев" — one count of one unit.
export function unitText(n, unit, lang) {
  const l = normLang(lang)
  return `${n} ${UNIT_WORD[l][unit](Number(n))}`
}

// The whole number of days in [startIso, endIso], both inclusive; null on bad input.
export function totalDays(startIso, endIso) {
  const s = parseIso(startIso), e = parseIso(endIso)
  if (!s || !e || cmp(e, s) < 0) return null
  return Math.round((utc(e) - utc(s)) / DAY_MS) + 1
}

// The interval as words: "6 თვე", "6 თვე და 12 დღე", "1 წელი", "18 დღე". Whole years are
// spoken as years only when `opts.unit === 'year'` (the agent chose that unit); otherwise
// 12 months stays "12 თვე", which is what the 12-month radio says. `opts.unit === 'day'`
// speaks the whole interval in days (`opts.days` = totalDays) — the agent typed a day count.
export function durationText(diff, lang, opts = {}) {
  if (!diff) return ''
  const l = normLang(lang)
  if (opts.unit === 'day' && Number.isInteger(opts.days) && opts.days >= 1) return unitText(opts.days, 'day', l)
  const { months, days } = diff
  const parts = []
  if (months >= 1) {
    if (opts.unit === 'year' && months % 12 === 0) parts.push(unitText(months / 12, 'year', l))
    else parts.push(unitText(months, 'month', l))
  }
  if (days >= 1 || parts.length === 0) parts.push(unitText(days, 'day', l))
  return parts.join(` ${AND[l]} `)
}

// ISO yyyy-mm-dd → dd.mm.yyyy (the contract's own date form); else passed through.
export function fmtDate(iso) {
  const m = ISO.exec(String(iso || ''))
  return m ? `${m[3]}.${m[2]}.${m[1]}` : String(iso || '')
}

// "6-თვიანი" / "1-წლიანი" / "18-დღიანი" — the Georgian adjective of a whole period; the
// English "6-month"; the Russian "срок 6 месяцев" is built in the sentence instead.
const KA_ADJ = { day: 'დღიანი', month: 'თვიანი', year: 'წლიანი' }
const EN_ADJ = { day: 'day', month: 'month', year: 'year' }

// The two generated lines under the rows:
//   short  — "20.02.2026 → 19.08.2026 · 6 თვე"
//   detail — a whole period: "6-თვიანი ვადა · ბოლო დღე: 19.08.2026"
//            a hand-set end:  "ხელით მითითებული დასრულების თარიღი · 6 თვე და 12 დღე"
// `opts.unit` = the unit the agent chose ('year' shows "1-წლიანი"; 'day' shows "18-დღიანი").
// Returns null when either date is missing or the end precedes the start.
export function describe(startIso, endIso, lang, opts = {}) {
  const diff = diffPeriod(startIso, endIso)
  if (!diff) return null
  const l = normLang(lang)
  const days = totalDays(startIso, endIso)
  const dur = durationText(diff, l, { unit: opts.unit, days })
  const short = `${fmtDate(startIso)} → ${fmtDate(endIso)} · ${dur}`
  // The whole period the detail line names: in the agent's unit when the interval fits it
  // (a day count always fits), else whole months, else nothing — the end was set by hand.
  const whole = opts.unit === 'day' ? { n: days, unit: 'day' } : asPeriod(diff, opts.unit === 'year' ? 'year' : 'month')
  let detail
  if (whole) {
    const last = fmtDate(endIso)
    if (l === 'ka') detail = `${whole.n}-${KA_ADJ[whole.unit]} ვადა · ბოლო დღე: ${last}`
    else if (l === 'ru') detail = `срок ${unitText(whole.n, whole.unit, 'ru')} · последний день: ${last}`
    else detail = `${whole.n}-${EN_ADJ[whole.unit]} term · last day: ${last}`
  } else if (l === 'ka') detail = `ხელით მითითებული დასრულების თარიღი · ${dur}`
  else if (l === 'ru') detail = `дата окончания указана вручную · ${dur}`
  else detail = `end date set by hand · ${dur}`
  return { short, detail, duration: dur, diff, exact: !!whole }
}
